import React, { useContext, useEffect, useState } from 'react';
import { Button, Drawer, Space } from 'antd';
import { ReactReduxContext } from 'react-redux';
import { store as appStore } from '../../store';
import { userSelector } from '../../store/user/slice';
import { formatActor, formatEventTime, UNKNOWN_ACTOR_SHORT } from '../../utils/audit-format';

/**
 * Phase 7C-6C rev.2 -- the two places an operator is allowed to appear.
 *
 * Every document screen in this app (สั่งซื้อ, รับเข้า, นับสต๊อก, เบิกสินค้า)
 * opens through this drawer, so the placement standard lives here once
 * rather than being re-implemented, and re-argued, on each screen.
 *
 * §6.1 UNSAVED record -- a small read-only line beside the save button
 *      naming the employee who is about to be recorded. It is a PREVIEW
 *      of the current session, never submitted and never authoritative:
 *      Backend stamps the actor from the session token (7C-6B), and this
 *      only tells the user which account they are about to sign with.
 *
 * §6.2 SAVED record -- the actor and time stored WITH that record, in the
 *      header metadata area above the document body, never inside the
 *      item rows.
 *
 * §19 The two must never be confused. If A creates a document today and B
 *      opens it tomorrow, B sees A. That is why the preview is limited to
 *      modes that create a new record, and why a saved record reads only
 *      from `recordActor`.
 */

interface recordActor {
    /** The operator stored with this record, from Backend. */
    name?: string | null;
    /** The authoritative timestamp stored with this record, from Backend. */
    at?: string | null;
}

interface props {
    mode: string,
    open: boolean,
    title: string,
    width: string,
    element?: React.ReactNode | null,
    onSaved: () => void,
    onClosed: () => void,
    saving?: boolean,
    /**
     * Actor/time belonging to a SAVED record. Omit for screens whose
     * Backend read contract carries no actor -- the header then says so
     * rather than borrowing the viewer's identity.
     */
    recordActor?: recordActor,
    /** Set false where a create form has no meaningful single operator. */
    showOperatorPreview?: boolean,
}

/** Modes that create a brand-new record, i.e. where a preview is truthful. */
const UNSAVED_MODES = ['ADD', 'MULTI', 'COPY'];
/** Modes that show a record that already exists in the ledger. */
const SAVED_MODES = ['VIEW', 'VIEW_ONLY', 'EDIT'];

const CommonDetail: React.FC<props> = (prop) => {

    const [open, setOpen] = useState(false);

    /**
     * The signed-in employee, read WITHOUT hard-requiring a <Provider>.
     *
     * `useSelector` throws outright when no Provider is above it, and this
     * drawer is a shared primitive that a dozen screens mount -- adopting
     * that requirement broke every existing test which renders one of those
     * screens on its own. `ReactReduxContext` returns null instead of
     * throwing, so the app's store is used when present and the singleton
     * otherwise (they are the same object in the running app).
     *
     * A render-time read rather than a subscription is enough here: the
     * drawer has `destroyOnClose`, so it re-mounts every time it opens, and
     * the signed-in employee cannot change while it is open.
     */
    const reduxContext = useContext(ReactReduxContext);
    const user = userSelector((reduxContext?.store ?? appStore).getState() as any);

    const onShow = () => {
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
        prop.onClosed();
    };

    const onSave = () => {
        prop.onSaved();
    }

    const isShowButton = (btn: string) => {
        if (btn === 'ADD') {
            return (prop.mode === 'ADD' || prop.mode === 'EDIT' || prop.mode === 'MULTI')
        } else if (btn === 'BACK') {
            return (prop.mode === 'VIEW' || prop.mode === 'VIEW_ONLY')
        } else if (btn === 'CANCEL') {
            return (prop.mode === 'ADD' || prop.mode === 'EDIT' || prop.mode === 'MULTI')
        }

        return false;
    }

    const isUnsaved = UNSAVED_MODES.includes(prop.mode);
    const isSaved = SAVED_MODES.includes(prop.mode);
    const showPreview = isUnsaved && (prop.showOperatorPreview ?? true);
    const showRecordMeta = isSaved && prop.recordActor !== undefined;

    useEffect(() => {
        if (prop.open) {
            onShow();
        } else {
            setOpen(false);
        }
    }, [prop.open])

    return (
        <>
            <Drawer
                title={prop.title}
                width={prop.width}
                placement="right"
                maskClosable={false}
                destroyOnClose={true}
                closable={false}
                onClose={onClose}
                open={open}
                footer={
                    <div className='detail-footer'>
                        {showPreview ? (
                            <span
                                className='detail-footer-actor'
                                data-testid='detail-operator-preview'
                                title='ผู้ใช้งานที่เข้าสู่ระบบอยู่ ระบบจะบันทึกผู้ทำรายการจากบัญชีนี้'
                            >
                                ผู้ทำรายการ: {formatActor(user?.name, UNKNOWN_ACTOR_SHORT)}
                            </span>
                        ) : <span />}
                        <Space>
                            { isShowButton('ADD') ? <Button className='btn-save' onClick={onSave} loading={prop.saving} disabled={prop.saving}>บันทึก</Button> : null }
                            { isShowButton('BACK') ? <Button className='btn-back' onClick={onClose}>ย้อนกลับ</Button> : null }
                            { isShowButton('CANCEL') ? <Button className='btn-cancel' onClick={onClose} disabled={prop.saving}>ยกเลิก</Button> : null }
                        </Space>
                    </div>
                }
            >
                {showRecordMeta ? (
                    <div className='detail-record-meta' data-testid='detail-record-meta'>
                        <div className='detail-record-meta-item'>
                            <span className='detail-record-meta-label'>ผู้ทำรายการ</span>
                            <span
                                className='detail-record-meta-value'
                                data-testid='detail-record-actor'
                            >
                                {formatActor(prop.recordActor?.name, UNKNOWN_ACTOR_SHORT)}
                            </span>
                        </div>
                        <div className='detail-record-meta-item'>
                            <span className='detail-record-meta-label'>วันที่-เวลา</span>
                            <span
                                className='detail-record-meta-value'
                                data-testid='detail-record-time'
                            >
                                {formatEventTime(prop.recordActor?.at)}
                            </span>
                        </div>
                    </div>
                ) : null}
                {prop.element}
            </Drawer>
        </>
    );
}

export default CommonDetail;
